export const AWS_WAF_TOKEN_HEADER = 'x-aws-waf-token';

const SCRIPT_ID = 'aws-waf-captcha-jsapi';
const DEFAULT_TOKEN_DOMAIN = 'shift-ev.com';

const normalizeIntegrationUrl = (url) => {
    const value = String(url || '').trim();
    if (!value) return '';
    return value.endsWith('/') ? value.slice(0, -1) : value;
};

export const getAwsWafCaptchaConfig = () => ({
    apiKey: String(process.env.REACT_APP_AWS_WAF_CAPTCHA_API_KEY || '').trim(),
    integrationUrl: normalizeIntegrationUrl(process.env.REACT_APP_AWS_WAF_INTEGRATION_URL),
    tokenDomain: String(process.env.REACT_APP_AWS_WAF_TOKEN_DOMAIN || DEFAULT_TOKEN_DOMAIN).trim(),
    tokenTransport: String(process.env.REACT_APP_AWS_WAF_TOKEN_TRANSPORT || 'cookie').trim().toLowerCase(),
});

export const shouldRequireAwsWafCaptcha = () => {
    const envValue = String(process.env.REACT_APP_AWS_WAF_CAPTCHA_REQUIRED || '').trim().toLowerCase();
    return envValue === 'true';
};

export const isAwsWafCaptchaConfigured = () => {
    const config = getAwsWafCaptchaConfig();
    return Boolean(config.apiKey && config.integrationUrl);
};

export const shouldSendAwsWafTokenHeader = () => (
    getAwsWafCaptchaConfig().tokenTransport === 'header'
);

export const buildAwsWafRequestConfig = (token) => {
    const requestConfig = {
        withCredentials: true,
    };

    if (shouldSendAwsWafTokenHeader()) {
        requestConfig.headers = {
            [AWS_WAF_TOKEN_HEADER]: token,
        };
    }

    return requestConfig;
};

export const getAwsWafCaptchaLocale = (language) => (
    String(language || '').trim().toLowerCase() === 'ar' ? 'ar-SA' : 'en-US'
);

export const loadAwsWafCaptchaScript = () => new Promise((resolve, reject) => {
    const config = getAwsWafCaptchaConfig();
    if (!config.integrationUrl) {
        reject(new Error('AWS WAF integration URL is not configured.'));
        return;
    }

    if (typeof window === 'undefined' || typeof document === 'undefined') {
        reject(new Error('AWS WAF CAPTCHA requires a browser environment.'));
        return;
    }

    if (config.tokenDomain) {
        window.awsWafCookieDomainList = [config.tokenDomain];
    }

    if (window.AwsWafCaptcha && window.AwsWafIntegration) {
        resolve();
        return;
    }

    const existingScript = document.getElementById(SCRIPT_ID);
    if (existingScript) {
        existingScript.addEventListener('load', () => resolve(), { once: true });
        existingScript.addEventListener('error', () => reject(new Error('Unable to load AWS WAF CAPTCHA.')), { once: true });
        return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.type = 'text/javascript';
    script.src = `${config.integrationUrl}/jsapi.js`;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Unable to load AWS WAF CAPTCHA.'));
    document.head.appendChild(script);
});

export const renderAwsWafCaptcha = (container, callbacks = {}, options = {}) => {
    const config = getAwsWafCaptchaConfig();
    if (!container) {
        throw new Error('AWS WAF CAPTCHA container is not available.');
    }
    if (!config.apiKey) {
        throw new Error('AWS WAF CAPTCHA API key is not configured.');
    }
    if (!window.AwsWafCaptcha?.renderCaptcha) {
        throw new Error('AWS WAF CAPTCHA API is not available.');
    }

    window.AwsWafCaptcha.renderCaptcha(container, {
        apiKey: config.apiKey,
        onSuccess: callbacks.onSuccess,
        onError: callbacks.onError,
        onLoad: callbacks.onLoad,
        onPuzzleCorrect: callbacks.onPuzzleCorrect,
        onPuzzleIncorrect: callbacks.onPuzzleIncorrect,
        onPuzzleTimeout: callbacks.onPuzzleTimeout,
        defaultLocale: options.defaultLocale,
        disableLanguageSelector: options.disableLanguageSelector,
        dynamicWidth: true,
        skipTitle: true,
    });
};

export const getAwsWafToken = async () => {
    if (!window.AwsWafIntegration?.getToken) {
        throw new Error('AWS WAF token API is not available.');
    }
    return window.AwsWafIntegration.getToken();
};
